import { useEffect, useLayoutEffect, useState, useMemo } from "react";
import * as React from "react";

import { useRoute } from "wouter";
import { cameraStore } from "../../appStore.js";
import { ref, useSnapshot } from "valtio";
import store from "../../appStore.js";
import useLevel from "../../hooks/useLevel.jsx";

import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { CameraControls, PerspectiveCamera } from "@react-three/drei";
import { useRef } from "react";
import { config, easings, useSpring } from "@react-spring/three";
import { useSynchronizedState, useMultiuserContext } from "@solidwhite/multiuser";

import { throttle } from "lodash-es";

import { useDeviceRotation } from "../../utils/useDeviceRotation.ts";

const invisibleCamControl = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight);

const camTargetPosition = new THREE.Vector3();
const camNormal = new THREE.Vector3();

const CameraWrapper = ({ rotateWithMouse = true, enabled = true }) => {
  const { presenting, awareness, broadcastMessage } = useMultiuserContext();

  const presentingRef = useRef(false);
  useEffect(() => {
    presentingRef.current = presenting;
  }, [presenting]);

  const { sideBarToggle, sideBarVisible, autoPlay } = useSnapshot(store);
  const camSnap = useSnapshot(cameraStore);

  const [fov, setFov] = useState(60);

  const cameraControlsRef = useRef<CameraControls>(null!);

  useEffect(() => {
    cameraStore.cameraControlsRef = ref(cameraControlsRef.current);
  }, []);

  const camTargetRef = useRef<THREE.Group>(null!);
  const camSubRotationXRef = useRef<THREE.Group>(null!);
  const camSubRotationYRef = useRef<THREE.Group>(null!);
  const camDistanceRef = useRef<THREE.Group>(null!);
  const camFinalRef = useRef<THREE.Group>(null!);
  const bbHelperRef = useRef<THREE.Mesh>(null!);

  const { camera } = useThree();

  useEffect(() => {
    const cameraTargetSettings = camSnap.cameraTargetSettings as unknown as THREE.Object3D;

    if (cameraTargetSettings.type) {
      const camSettingsObject = cameraTargetSettings;

      const targetWorldPosition = new THREE.Vector3();
      const camWorldPostion = new THREE.Vector3();

      camSettingsObject.getWorldPosition(targetWorldPosition);
      camSettingsObject.children[0].getWorldPosition(camWorldPostion);

      cameraControlsRef.current?.setLookAt(
        camWorldPostion.x,
        camWorldPostion.y,
        camWorldPostion.z,
        targetWorldPosition.x,
        targetWorldPosition.y,
        targetWorldPosition.z,
        true,
      );

      if (camSettingsObject.userData.maxAzimuthAngle) {
        //@ts-ignore
        let settings: {
          minDistance: number;
          maxDistance: number;
          minAzimuthAngle: number;
          maxAzimuthAngle: number;
          minPolarAngle: number;
          maxPolarAngle: number;
          clampAzimuth: boolean;
          boundsMinX: number;
          boundsMinY: number;
          boundsMinZ: number;
          boundsMaxX: number;
          boundsMaxY: number;
          boundsMaxZ: number;
          planarDrag: boolean;
          useBounds: boolean;
          fov: number;
        } = camSettingsObject.userData;

        cameraControlsRef.current.maxDistance = settings.maxDistance;
        cameraControlsRef.current.minDistance = settings.minDistance;

        if (settings.fov) {
          setFov(settings.fov);
        } else {
          setFov(50);
        }

        const spherical = new THREE.Spherical().setFromVector3(camWorldPostion.clone().sub(targetWorldPosition));

        const minPolar = THREE.MathUtils.radToDeg(spherical.phi) - settings.minPolarAngle;
        const maxPolar = THREE.MathUtils.radToDeg(spherical.phi) + settings.maxPolarAngle;
        cameraControlsRef.current.minPolarAngle = THREE.MathUtils.degToRad(minPolar);
        cameraControlsRef.current.maxPolarAngle = THREE.MathUtils.degToRad(maxPolar);

        if (settings.clampAzimuth) {
          let azimuthTarget = THREE.MathUtils.radToDeg(spherical.theta);
          if (azimuthTarget < 0) {
            azimuthTarget = 360 + azimuthTarget;
          }
          const minAzimuth = azimuthTarget - settings.minAzimuthAngle;
          const maxAzimuth = azimuthTarget + settings.maxAzimuthAngle;

          cameraControlsRef.current.minAzimuthAngle = THREE.MathUtils.degToRad(minAzimuth);
          cameraControlsRef.current.maxAzimuthAngle = THREE.MathUtils.degToRad(maxAzimuth);
        } else {
          cameraControlsRef.current.minAzimuthAngle = -Infinity;
          cameraControlsRef.current.maxAzimuthAngle = Infinity;
        }

        let boundsMin = new THREE.Vector3(-settings.boundsMaxX, settings.boundsMinY, settings.boundsMinZ);
        let boundsMax = new THREE.Vector3(-settings.boundsMinX, settings.boundsMaxY, settings.boundsMaxZ);

        let worldPos = new THREE.Vector3();
        cameraTargetSettings.getWorldPosition(worldPos);

        if (settings.useBounds) {
          boundsMin.add(worldPos);
          boundsMax.add(worldPos);

          let bb = new THREE.Box3(boundsMin, boundsMax);
          cameraControlsRef.current.setBoundary(bb);

          let center = new THREE.Vector3();
          let size = new THREE.Vector3();
          bb.getCenter(center);
          bb.getSize(size);
          bbHelperRef.current.position.copy(center);
          bbHelperRef.current.scale.x = size.x;
          bbHelperRef.current.scale.y = size.y;
          bbHelperRef.current.scale.z = size.z;
          // bbHelperRef.current.visible = true;
        } else {
          cameraControlsRef.current.setBoundary(undefined);
          // bbHelperRef.current.visible = false;
        }

        if (settings.planarDrag) {
          cameraControlsRef.current.verticalDragToForward = true;
        } else {
          cameraControlsRef.current.verticalDragToForward = false;
        }
      } else {
        cameraControlsRef.current.maxDistance = 50;
        cameraControlsRef.current.minDistance = 0.1;
        cameraControlsRef.current.minAzimuthAngle = -Infinity;
        cameraControlsRef.current.maxAzimuthAngle = Infinity;
        cameraControlsRef.current.minPolarAngle = THREE.MathUtils.degToRad(10);
        cameraControlsRef.current.maxPolarAngle = THREE.MathUtils.degToRad(120);
        cameraControlsRef.current.setBoundary(undefined);
        cameraControlsRef.current.verticalDragToForward = false;
      }
    }
  }, [camSnap.cameraTargetSettings]);

  // useFrame(() => {
  //   console.log(THREE.MathUtils.radToDeg(cameraControlsRef.current.polarAngle));
  // });

  //FOV spring
  useSpring({
    fov: fov,
    onChange: ({ value }) => {
      //@ts-ignore
      camera.fov = value.fov;
      camera.updateProjectionMatrix();
    },
    config: config.molasses,
    // config: { duration: 400, easing: easings.easeOutCirc },
  });

  const setViewOffset = (value: number) => {
    let offset = value * 520 * 0.5;
    let xOffset = (window.innerWidth / 1920) * offset;

    camera.setViewOffset(window.innerWidth, window.innerHeight, xOffset, 0, window.innerWidth, window.innerHeight);
    camera.updateProjectionMatrix();
  };

  //view offset spring
  useSpring({
    viewOffset: sideBarVisible && !autoPlay ? (sideBarToggle ? 0 : 1) : 0,
    onChange: ({ value }) => {
      setViewOffset(value.viewOffset);
    },
    config: config.molasses,
  });

  useFrame(({ camera }) => {
    camNormal.set(0, 0, 1).applyQuaternion(cameraControlsRef.current.camera.quaternion);
    camNormal.normalize().multiplyScalar(cameraControlsRef.current.distance);
    camTargetPosition.copy(cameraControlsRef.current.camera.position.clone().sub(camNormal));

    camSubRotationYRef.current.position.copy(camTargetPosition);
    camTargetRef.current.quaternion.copy(cameraControlsRef.current.camera.quaternion);

    camDistanceRef.current.position.set(0, 0, cameraControlsRef.current.distance);

    if (camFinalRef.current && enabled) {
      const camFinalPos = new THREE.Vector3();
      camFinalRef.current.getWorldPosition(camFinalPos);
      const camFinalQuat = new THREE.Quaternion();
      camFinalRef.current.getWorldQuaternion(camFinalQuat);

      camera.position.copy(camFinalPos);
      camera.quaternion.copy(camFinalQuat);
    }
  });

  useDeviceRotation((beta, gamma) => {
    const range = 0.25;
    const x = (beta - 0) * range * -1; // In degree in the range [-180,180]
    const y = gamma * range * -1; // In degree in the range [-90,90]

    camSubRotationXRef.current?.rotation.set(
      THREE.MathUtils.degToRad(x) * (rotateWithMouse ? (presentingRef.current ? 0.15 : 1) : 0),
      0,
      0,
      "XYZ",
    );

    camSubRotationYRef.current?.rotation.set(
      0,
      THREE.MathUtils.degToRad(y) * (rotateWithMouse ? (presentingRef.current ? 0.15 : 1) : 0),
      0,
      "XYZ",
    );
  }, 25);

  useFrame(() => {
    if (presenting) {
      throttledSend();
    }
  });

  const throttledSend = useMemo(() => {
    return throttle(() => {
      if (!camSubRotationYRef) return;

      // let yRot = new THREE.Quaternion();
      // yRot.copy(camSubRotationYRef.current.quaternion);

      // let xRot = new THREE.Quaternion();
      // xRot.copy(camTargetRef.current.quaternion);
      // xRot.multiply(camSubRotationXRef.current.quaternion);

      // console.log({
      //   t: camTargetPosition.toArray(),
      //   y: yRot.toArray(),
      //   x: camSubRotationXRef.current.quaternion.toArray(),
      //   fov: camera.fov,
      // });

      // cameraControlsRef.current.normalizeRotations();
      // cameraControlsRef.current.setLookAt();

      // awareness?.setLocalStateField("cam", {
      //   t: camSubRotationYRef.current.position.toArray(),
      //   ct: cameraControlsRef.current.camera.position.toArray(),
      //   y: camSubRotationYRef.current.quaternion.toArray(),
      //   x: camSubRotationXRef.current.quaternion.toArray(),
      //   fov: camera.fov,
      // });

      broadcastMessage("cam", {
        t: camSubRotationYRef.current.position.toArray(),
        ct: cameraControlsRef.current.camera.position.toArray(),
        y: camSubRotationYRef.current.quaternion.toArray(),
        x: camSubRotationXRef.current.quaternion.toArray(),
        fov: camera.fov,
      });
    }, 33);
  }, [broadcastMessage]);

  return (
    <group>
      <mesh ref={bbHelperRef} visible={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial transparent wireframe color="red" depthTest={false} />
      </mesh>
      <group ref={camSubRotationYRef}>
        <group ref={camTargetRef}>
          <group ref={camSubRotationXRef}>
            <group ref={camDistanceRef}>
              <group ref={camFinalRef}></group>
              {/* <PerspectiveCamera
                  makeDefault
                  ref={renderCamRef}
                  near={0.03}
                  far={500}
                /> */}
            </group>
          </group>
        </group>
      </group>

      <CameraControls
        ref={cameraControlsRef}
        smoothTime={0.35}
        camera={invisibleCamControl}
        makeDefault={false}
        verticalDragToForward={false}
        enabled={camSnap.enabled}
        // minAzimuthAngle={THREE.MathUtils.degToRad(360-90)}
        // maxAzimuthAngle={THREE.MathUtils.degToRad(360+90)}
      />
    </group>
  );
};

export default CameraWrapper;
